import React, { useEffect, useState } from "react";
import PasswordInput from "./PasswordInput";

import { API_BASE as API } from "../utils/apiBase";

async function apiJson(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || data?.message || "Request failed");
  return data;
}

function getEmailFromLocation() {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.href);
  return u.searchParams.get("email") || "";
}

export default function ForgotPassword({ initialEmail = "", onBackToLogin }) {
  const [step, setStep] = useState("request"); // request | confirm
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    if (!initialEmail) setEmail(getEmailFromLocation());
  }, [initialEmail]);

  async function requestCode() {
    setStatus({ type: "", message: "" });
    setPending(true);
    try {
      await apiJson("/auth/password/reset/request", {
        method: "POST",
        body: { email },
      });
      setStatus({
        type: "success",
        message: "If the account exists, a reset code was sent (check spam too).",
      });
      setStep("confirm");
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setPending(false);
    }
  }

  async function confirmReset() {
    setStatus({ type: "", message: "" });

    if (!email || !code || !newPw || !newPw2) {
      setStatus({ type: "error", message: "Please fill out all fields." });
      return;
    }
    if (newPw !== newPw2) {
      setStatus({ type: "error", message: "New password and confirmation do not match." });
      return;
    }

    setPending(true);
    try {
      await apiJson("/auth/password/reset/confirm", {
        method: "POST",
        body: { email, code, new_password: newPw },
      });
      setStatus({ type: "success", message: "Password updated. Redirecting to login…" });
      setTimeout(() => {
        if (onBackToLogin) onBackToLogin();
        else window.location.assign("/");
      }, 900);
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="p-4">
      <div className="max-w-sm mx-auto p-4 space-y-4 border rounded">
        <h2 className="text-2xl font-semibold">Reset password</h2>

        {status.message ? (
          <div className={status.type === "error" ? "text-red-600 dark:text-red-400" : "text-green-700 dark:text-green-300"}>
            {status.message}
          </div>
        ) : null}

        <input
          type="email"
          className="w-full border rounded p-2"
          placeholder="Email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {step === "request" ? (
          <>
            <button
              type="button"
              disabled={pending || !email}
              onClick={requestCode}
              className="w-full bg-blue-600 text-white rounded-xl py-2 disabled:opacity-60"
            >
              {pending ? "Sending…" : "Send reset code"}
            </button>
          </>
        ) : (
          <>
            <input
              className="w-full border rounded p-2"
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            {/* best practice for password managers */}
            <input type="text" value={email} readOnly autoComplete="username" className="hidden" tabIndex={-1} />

            <PasswordInput
              className="w-full border rounded p-2"
              placeholder="New password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
            <PasswordInput
              className="w-full border rounded p-2"
              placeholder="Confirm new password"
              autoComplete="new-password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
            />

            <button
              type="button"
              disabled={pending || !email || !code || !newPw || !newPw2}
              onClick={confirmReset}
              className="w-full bg-blue-600 text-white rounded-xl py-2 disabled:opacity-60"
            >
              {pending ? "Updating…" : "Update password"}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => {
            if (onBackToLogin) return onBackToLogin();
            if (window.history.length > 1) return window.history.back();
            window.location.assign("/login");
          }}
          className="w-full border rounded-xl py-2 text-center hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center justify-center gap-2"
          disabled={pending}
          title="Back to login"
          aria-label="Back to login"
        >
          <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
        </button>

        <div className="text-xs text-gray-600 dark:text-gray-300">
          Password must be 12+ chars and include upper, lower, number, and special character.
        </div>
      </div>
    </div>
  );
}
